import React, { useState } from 'react';
import axios from 'axios';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useToast } from './Toast';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import RealtimeWidget from './RealtimeWidget';
import SummaryCards from './SummaryCards';
import VisitorsChart from './VisitorsChart';
import TrafficSources from './TrafficSources';
import TopPages from './TopPages';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = ({ onDisconnect }) => {
  const [dateRange, setDateRange] = useState('30daysAgo');
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  
  const { token } = useAuthToken();
  const { showToast } = useToast();
  
  const {
    summary,
    chartData,
    trafficSources,
    topPages,
    devices,
    realtimeUsers,
    isLoading,
    error,
    refetch
  } = useAnalyticsData(dateRange);

  const dateRangeOptions = [
    { value: '7daysAgo', label: '7 days' },
    { value: '30daysAgo', label: '30 days' },
    { value: '90daysAgo', label: '90 days' }
  ];

  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      
      // Get business_id from localStorage
      const businessId = localStorage.getItem('user_id');
      if (!businessId) {
        showToast('Business ID not found. Please log in again.', 'error');
        setIsDisconnecting(false);
        return;
      }

      if (!token) {
        showToast('Authentication token not available. Please refresh the page.', 'error');
        setIsDisconnecting(false);
        return;
      }
      
      const response = await axios.post(`${process.env.REACT_APP_APIURL}analytics/disconnect`, {
        business_id: businessId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showToast('Google Analytics disconnected', 'success');
        onDisconnect();
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      showToast(err.response?.data?.message || 'Failed to disconnect', 'error');
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  return (
    <div className="ga-dashboard-wrapper">
      <div className="ga-dashboard-topbar">
        <div>
          <div className="ga-dashboard-heading">Analytics Overview</div>
          <div className="ga-dashboard-subheading">Track your store's performance</div>
        </div>
        
        <div className="ga-daterange-group">
          {dateRangeOptions.map(option => (
            <button
              key={option.value}
              className={`ga-daterange-btn ${dateRange === option.value ? 'active' : ''}`}
              onClick={() => setDateRange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        
        <button
          className="ga-disconnect-btn"
          onClick={() => setShowDisconnectConfirm(true)}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Disconnect Google
        </button>
      </div>

      {showDisconnectConfirm && (
        <div className="ga-disconnect-confirm">
          <div className="ga-disconnect-confirm-text">
            Are you sure? This will disconnect Google Analytics from your store.
          </div>
          <div className="ga-confirm-actions">
            <button
              className="ga-confirm-cancel-btn"
              onClick={() => setShowDisconnectConfirm(false)}
            >
              Cancel
            </button>
            <button
              className="ga-confirm-yes-btn"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Disconnecting...' : 'Yes, disconnect'}
            </button>
          </div>
        </div>
      )}

      <RealtimeWidget activeUsers={realtimeUsers} />
      
      <SummaryCards summary={summary} isLoading={isLoading} />
      
      <VisitorsChart 
        chartData={chartData} 
        isLoading={isLoading} 
        dateRange={dateRange}
      />
      
      <div className="ga-two-col">
        <TrafficSources sources={trafficSources} isLoading={isLoading} />
        <TopPages pages={topPages} isLoading={isLoading} />
      </div>

      {error && (
        <div className="ga-error-bar">
          <span>Could not load analytics data. {error}</span>
          <button className="ga-retry-btn" onClick={refetch}>
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;